// The editable form of one step (node) of the Max's script, and the way back to the node the server keeps.
//
//   const form = toForm(record.node);        // what the step's fields edit
//   const node = toNode(form, record.node);  // what is sent to PATCH scenario_nodes/:id
//
// The server's node is the source: whatever the form does not edit (a fact's `class`, `skip_if_answered`, a
// `return` value, a `channels` object) goes back as it came. Lists are copied, never shared with the store.

const list = value => (Array.isArray(value) ? value.map(String) : []);
const text = value => (typeof value === 'string' ? value : '');

export const NEXT_KINDS = ['set', 'push', 'return'];

let rowSeed = 0;
// A stable identity for a "next" row: its place in the list changes when rows are added or removed.
export const newRowId = () => {
  rowSeed += 1;
  return rowSeed;
};

const nextKind = row => NEXT_KINDS.find(kind => kind in row) || 'set';

export const toForm = (node = {}) => {
  const questions = {};
  Object.entries(node.questions || {}).forEach(([key, question]) => {
    questions[key] = {
      ask: list(question?.ask),
      answerHint: text(question?.answer_hint),
    };
  });
  // every fact the step asks about has a question entry to edit, even when the server has none yet
  [...list(node.required_facts), ...list(node.optional_facts)].forEach(key => {
    questions[key] ||= { ask: [], answerHint: '' };
  });
  return {
    goal: text(node.goal),
    say: Array.isArray(node.say) ? list(node.say) : [text(node.say)],
    questions,
    requiredFacts: list(node.required_facts),
    optionalFacts: list(node.optional_facts),
    avoid: list(node.avoid),
    listen: node.listen !== false,
    silenceTimeoutS: node.silence_timeout_s ?? null,
    tools: list(node.tools),
    next: (node.next || []).map((row, index) => {
      const kind = nextKind(row);
      return {
        uid: newRowId(),
        // the row of the server's list this one came from (null: a new row), to give back what it carries
        from: index,
        when: text(row.when),
        kind,
        target: kind === 'return' ? '' : text(row[kind]),
      };
    }),
    channels: Array.isArray(node.channels) ? list(node.channels) : [],
  };
};

const clean = items => items.map(item => item.trim()).filter(Boolean);

const blank = value =>
  value === '' ||
  value === null ||
  value === undefined ||
  (Array.isArray(value) && !value.length) ||
  (typeof value === 'object' &&
    !Array.isArray(value) &&
    !Object.keys(value).length);

// A key the server did not have and nobody filled in stays out, so that an untouched step goes back as it came.
const put = (node, key, value, original) => {
  if (blank(value) && !(key in original)) delete node[key];
  else node[key] = value;
};

export const toNode = (form, original = {}) => {
  const questions = {};
  Object.entries(form.questions).forEach(([key, question]) => {
    const before = original.questions?.[key];
    const ask = clean(question.ask);
    const hint = question.answerHint.trim();
    // a question the server did not have and nobody wrote stays out
    if (!ask.length && !hint && !before) return;
    const entry = { ...(before || {}), ask };
    put(entry, 'answer_hint', hint, before || {});
    questions[key] = entry;
  });
  const say = clean(form.say);
  const node = { ...original };
  put(node, 'goal', form.goal.trim(), original);
  // a single phrase stays a string when the server kept it as one
  put(
    node,
    'say',
    typeof original.say === 'string' && say.length <= 1 ? say[0] || '' : say,
    original
  );
  put(node, 'questions', questions, original);
  put(node, 'required_facts', form.requiredFacts, original);
  put(node, 'optional_facts', form.optionalFacts, original);
  put(node, 'avoid', clean(form.avoid), original);
  if (form.listen === true && !('listen' in original)) delete node.listen;
  else node.listen = form.listen;
  const silence =
    form.silenceTimeoutS === null || form.silenceTimeoutS === ''
      ? original.silence_timeout_s
      : Number(form.silenceTimeoutS);
  if (silence === undefined) delete node.silence_timeout_s;
  else node.silence_timeout_s = silence;
  put(node, 'tools', form.tools, original);
  put(
    node,
    'next',
    form.next.map(row => {
      const before = original.next?.[row.from] || {};
      const entry = { ...before, when: row.when.trim() };
      NEXT_KINDS.forEach(kind => delete entry[kind]);
      entry[row.kind] =
        row.kind === 'return' ? (before.return ?? true) : row.target;
      return entry;
    }),
    original
  );
  if (Array.isArray(original.channels) || form.channels.length) {
    node.channels = form.channels;
  }
  return node;
};

// Dirty is a difference from the form the server's node gives, not from the raw node: the form fills in empty
// lists and texts that the node never had.
export const isDirty = (form, title, record) =>
  title !== (record.title || '') ||
  JSON.stringify(toNode(form, record.node)) !==
    JSON.stringify(toNode(toForm(record.node), record.node));

// Errors of a field: the ones the server put on the node and the ones the whole-set validation found for it.
// A field matches its own path and the paths under it ("questions.scope.ask" belongs to "questions").
export const errorsFor = (record, validation, field) => {
  const all = [
    ...(record.errors || []),
    ...(validation?.errors || [])
      .filter(error => [record.node_key, record.id].includes(error.node))
      .map(({ field: path, message }) => ({ field: path, message })),
  ];
  const seen = new Set();
  return all.filter(error => {
    const matches =
      error.field === field ||
      String(error.field || '').startsWith(`${field}.`);
    const key = `${error.field}|${error.message}`;
    if (!matches || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const hasErrors = (record, validation) =>
  (record.errors || []).length > 0 ||
  (validation?.errors || []).some(error =>
    [record.node_key, record.id].includes(error.node)
  );

// A predicate of the catalog is a word or an object; the chip shows its label and inserts its text.
export const predicateChips = (predicates = []) =>
  predicates
    .map(predicate => {
      if (typeof predicate === 'string') {
        return { text: predicate, label: predicate };
      }
      const value =
        predicate?.example ||
        predicate?.id ||
        predicate?.key ||
        predicate?.label;
      return value ? { text: value, label: predicate.label || value } : null;
    })
    .filter(Boolean);

// The quick test of "answer hint" runs in the browser, on demand, over short texts. A pattern is a way to freeze
// the page (catastrophic backtracking), so the size is limited and a repetition of a repetition is refused before
// it is ever compiled. Returns { state: 'empty'|'invalid'|'unsafe'|'too_long'|'match'|'miss' }.
export const HINT_LIMIT = 200;

// "(a+)+", "(a*)*", "(a+)*", "(.+){2,}": a group that repeats something repeated, or alternatives that overlap.
const NESTED_QUANTIFIER =
  /\((?:[^()\\]|\\.)*[+*}](?:[^()\\]|\\.)*\)\s*(?:[+*]|\{\d+,\d*\})/;

export const hintUnsafe = pattern => NESTED_QUANTIFIER.test(pattern);

export const testHint = (pattern, sample) => {
  if (!pattern.trim()) return { state: 'empty' };
  if (pattern.length > HINT_LIMIT || (sample || '').length > HINT_LIMIT) {
    return { state: 'too_long' };
  }
  if (hintUnsafe(pattern)) return { state: 'unsafe' };
  let regex;
  try {
    regex = new RegExp(pattern, 'i');
  } catch {
    return { state: 'invalid' };
  }
  if (!sample) return { state: 'empty' };
  const found = sample.match(regex);
  return found ? { state: 'match', matched: found[0] } : { state: 'miss' };
};

// The value in `answer_hint` is a hint in words as often as a pattern: only tell "invalid" when it looks like one.
export const looksLikePattern = value => /[\\^$*+?()[\]{}|]/.test(value);
